import {AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {AuthService} from '../../shared/services/auth.service';
import {Profile} from '../../shared/interface/profile';
import {of, ReplaySubject} from 'rxjs';
import {delay, first, skip, takeUntil, tap} from 'rxjs/operators';
import {Config} from '../sidebar/sidebar.component';
import {ActivatedRoute, Router, RouterEvent} from '@angular/router';
import {UserApplications, UserPermissions} from '../../shared/interface/user-permissions';
import {MatDialog} from '@angular/material/dialog';
import {ChangePasswordDialogComponent} from '../user/change-password-dialog/change-password-dialog.component';
import {ProfileDialogComponent} from '../user/profile-dialog/profile-dialog.component';
import {
  GoogleAuthenticatorDialogComponent
} from '../user/google-authenticator-dialog/google-authenticator-dialog.component';
import {NotificationService} from '../../shared/services/notification.service';
import {CountedAndPaginated} from '../../shared/interface/response/counted-and-paginated';
import {Notification} from '../../shared/interface/notification';
import {SubmissionsComponent} from "../submissions/submissions.component";
import {HttpErrorResponse} from "@angular/common/http";
import {NewsletterService} from "../../shared/services/newsletter.service";
import {NewsletterPdfViewComponent} from "../newsletter/newsletter-pdf-view/newsletter-pdf-view.component";
import {src} from "../../../environments/environment";
import {CalendarComponent} from "../meetings/calendar/calendar.component";
import {CalendarDialogComponent} from "../meetings/calendar-dialog/calendar-dialog.component";
import {EmailSendJobListComponent} from "../email-send/email-send-job-list/email-send-job-list.component";

export interface NavbarConfig {
  name: string;
  icon: string;
  sidebarConfigs: Array<Config>;
  route?: string;
  disabled?: boolean;
  url?: string;
  link?: string;
}

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss']
})
export class NavbarComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() leftNavToggle = new EventEmitter();
  @Output() sidebarConfig = new EventEmitter<Array<Config>>();

  profile?: Profile;
  userPermissions: ReplaySubject<UserPermissions> = new ReplaySubject<UserPermissions>(1);
  userApplications: ReplaySubject<UserApplications> = new ReplaySubject<UserApplications>(1);
  currentConfig?: NavbarConfig = null;
  pathsConfig: Array<NavbarConfig> = [];
  pathsConfigSubject: ReplaySubject<Array<NavbarConfig>> = new ReplaySubject<Array<NavbarConfig>>(1);
  notifications: ReplaySubject<CountedAndPaginated<Notification>>;
  notificationsPage = 0;
  isDisabled = false;
  disableLinks = false;
  controlText = '';
  notificationsPageSize = 5;
  readonly src = src;


  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    public router: Router,
    private auth: AuthService,
    private dialog: MatDialog,
    public notificationService: NotificationService,
    private newsletterService: NewsletterService
  ) {
    this.userApplications = auth.getUserApplications();
    router.events
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val: RouterEvent) => {
        if (!val.url) {
          return;
        }
        this.pathsConfigSubject
          .pipe(first())
          .subscribe(() => {
            if (window.location.href.includes('szkolenie/kalendarz')){
              this.isDisabled = true;
            } else {
              this.isDisabled = false;
            }
            this.pathsConfig.forEach((navbarConfig: NavbarConfig) => {
              navbarConfig.sidebarConfigs.forEach((config: Config) => {
                if (val.url.startsWith(config.parentPath)) {
                  config.show = true;
                  this.sidebarConfig.emit(navbarConfig.sidebarConfigs);
                } else {
                  config.show = false;
                }
              });
            });
          });
      });

    this.notifications = this.notificationService.notifications;
  }

  ngOnInit(): void {
    this.auth.getProfile()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((profile: Profile) => {
        this.profile = profile;
      });

    this.auth.getUserPermissions()
      .pipe(
        takeUntil(this.destroyed$),
        skip(1)
      )
      .subscribe((permissions: UserPermissions) => {
        this.userPermissions.next(permissions);
        this.auth.getUserApplications()
          .pipe(
            takeUntil(this.destroyed$),
            skip(1)
          )
          .subscribe((applications: UserApplications) => {
            this.createConfig(permissions, applications);
          });
      });

    this.notifications
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.notificationService.updateAllAsRead());
  }


  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngAfterViewInit(): void {
    of('')
      .pipe(
        delay(0),
        tap(() => {
          if (this.router.url === '/') {
            this.pathsConfigSubject
              .pipe(first())
              .subscribe(() => {
                this.sidebarConfig.emit(this.pathsConfig[0].sidebarConfigs);
                this.currentConfig = this.pathsConfig[0];
              });
          }
        })
      ).subscribe();
  }

  toggleLeftNav(): void {
    this.leftNavToggle.emit();
  }

  logout(): void {
    this.auth.logout();
  }

  changeSidebar(config: NavbarConfig): void {
    this.sidebarConfig.emit(config.sidebarConfigs);
    this.currentConfig = config;
  }

  openChangePasswordDialog(): void {
    this.dialog.open(ChangePasswordDialogComponent, {
      width: '50vw',
      maxHeight: '90vh',
    });
  }

  openProfileDialog(): void {
    this.dialog.open(ProfileDialogComponent, {
      width: '50vw',
      maxHeight: '90vh',
    });
  }

  openGoogleAuthenticatorDialog(): void {
    this.dialog.open(GoogleAuthenticatorDialogComponent, {
      width: '50vw',
      maxHeight: '90vh',
    });
  }

  loadNotifications(): void {
    this.notificationService.loadList(this.notificationsPage, this.notificationsPageSize, {});
  }

  previousPage(): void {
    this.notificationsPage--;
    this.loadNotifications();
  }
  deleteNotifications(notification: Notification): void {
    this.notificationService.delete(notification.uuid)
      .subscribe({
        next: () => {
        },
        error: (error: HttpErrorResponse) => {
        }
      });
    this.loadNotifications();
  }
  deleteNotificationsAll(): void {
      this.notificationService.deleteAll()
        .subscribe({
          next: () => {
          },
          error: (error: HttpErrorResponse) => {
          }
        });
    this.loadNotifications();
    location.reload();
  }

  nextPage(): void {
    this.notificationsPage++;
    this.loadNotifications();
  }

  downloadFile(notification: Notification): void {
    this.notificationService.downloadFile(notification.uuid);
  }
  controlTest(): boolean{
    if ( window.location.href.includes('http://127.0.0.1') ||  window.location.href.includes('http://localhost')  ){
      this.controlText = 'DEV';
      return true;
    }
    if ( window. location.href.includes('test-1.asf24.pl')){
      this.controlText = 'Test';
      return true;
    }
    if ( window. location.href.includes('test-2.asf24.pl')){
      this.controlText = 'Test';
      return true;
    }
    return false;
  }

  checkProd(): boolean{
    if ( window. location.href.includes('test-1.asf24.pl')){
      this.controlText = 'test-1.asf24.pl';
      return true;
    }
    return false;
  }

  openDialog(): void {
    const dialogRef = this.dialog.open(SubmissionsComponent, {
      width: '60%',

    });
  }

  openCalendar(): void {
    const dialogRef = this.dialog.open(CalendarDialogComponent, {
      width: '90%',
      maxHeight: '90vh',
    });
  }

  viewNewsletter(uuid): void{
    this.newsletterService.get(uuid)
      .subscribe((newsletter) => {
        const dialogRef = this.dialog.open(NewsletterPdfViewComponent, {
          width: '80vw',
          maxHeight: '80vh',
          data: {
            newsletterObject: newsletter
          },
        });
      });

  }
  private createConfig(userPermissions: UserPermissions, userApplications: UserApplications): void {
    this.pathsConfig = [
      {
        name: 'Struktura sieci',
        icon: 'groups',
        route: '/partnerzy',
        sidebarConfigs: [
          {
            title: 'Notatki',
            show: false,
            icon: 'edit_note',
            parentPath: '/notatki',
            disabled: !userPermissions.note.limited_read,
            items: [
              {
                icon: 'add',
                route: '/notatki/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.note.limited_read,
              },
              {
                icon: 'list',
                route: '/notatki',
                title: 'Lista',
                disabled: !userPermissions.note.limited_read,
              },
            ]
          },
          {
            title: 'Licencje Agent21',
            show: false,
            icon: 'content_copy',
            parentPath: '/licencje-agent21',
            disabled: !userPermissions.licensed_agent.read,
            items: [
             {
               icon: 'list',
               route: '/licencje-agent21',
               title: 'Lista',
               disabled: !userPermissions.licensed_agent.read,
             },
            ]
          },
          {
            title: 'Partnerzy',
            show: false,
            icon: 'account_circle',
            parentPath: '/partnerzy',
            disabled: !userPermissions.partner.read,
            items: [
              {
                icon: 'add',
                route: '/partnerzy/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.partner.write,
              },
              {
                icon: 'list',
                route: '/partnerzy',
                title: 'Lista',
                disabled: !userPermissions.partner.read,
              },
              {
                icon: 'search',
                route: '/partnerzy/wyszukiwarka',
                title: 'Wyszukiwarka',
                disabled: !userPermissions.partner_search_engine.read,
                hidden: !userPermissions.partner_search_engine.read,
              },
              {
                icon: 'block',
                route: '/partnerzy/zablokowane-nip',
                title: 'Zablokowane NIP',
                disabled: !userPermissions.partner.read,
                hidden: !userPermissions.partner.read,
              },
            ]
          },
          {
            title: 'Placówki',
            show: false,
            icon: 'home',
            parentPath: '/placowki',
            disabled: !userPermissions.facility.read,
            items: [
              {
                icon: 'add',
                route: '/placowki/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.facility.write,
              },
              {
                icon: 'list',
                route: '/placowki',
                title: 'Lista',
                disabled: !userPermissions.facility.read,
              }
            ]
          },
          {
            title: 'OFWCA',
            show: false,
            icon: 'group',
            parentPath: '/ofwca',
            disabled: !userPermissions.ofwca.limited_read,
            items: [
              {
                icon: 'add',
                route: '/ofwca/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.ofwca.write,
              },
              {
                icon: 'list',
                route: '/ofwca',
                title: 'Lista',
                disabled: !userPermissions.ofwca.limited_read,
              }
            ]
          },
          {
            title: 'TU',
            show: false,
            icon: 'business',
            items: [],
            route: '/tu',
            parentPath: '/tu',
            disabled: !userPermissions.insurance_company.read,
          },
          {
            title: 'Dokumenty',
            show: false,
            icon: 'file_present',
            parentPath: '/dokumenty',
            disabled: !userPermissions.document.read,
            items: [
              {
                icon: 'add',
                route: '/dokumenty/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.document.write,
              },
              {
                icon: 'list',
                route: '/dokumenty',
                title: 'Lista',
                disabled: !userPermissions.document.read,
              },
              {
                icon: 'add',
                route: '/dokumenty/szablony/dodaj',
                title: 'Szablony dodaj',
                disabled: !userPermissions.template.write,
              },
              {
                icon: 'list',
                route: '/dokumenty/szablony',
                title: 'Szablony lista',
                disabled: !userPermissions.template.read,
              },
              {
                icon: 'fact_check',
                route: '/dokumenty/szablony/konfiguracja',
                title: 'Szablony lista pól',
                disabled: !userPermissions.template.read,
              }
            ]
          },
          {
            title: 'Umowy',
            show: false,
            icon: 'history_edu',
            parentPath: '/umowy',
            disabled: !userPermissions.contract.read,
            items: [
              {
                icon: 'add',
                route: '/umowy/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.contract.create,
              },
              {
                icon: 'list',
                route: '/umowy',
                title: 'Lista',
                disabled: !userPermissions.contract.read,
              },
              {
                icon: 'list',
                route: '/umowy/zgloszenia-samofakturowanie',
                title: 'Zgłoszenia samofakturowanie',
                disabled: !userPermissions.contract.delete,
                hidden: !userPermissions.contract.delete,
              },
              {
                icon: 'list',
                route: '/umowy/zalaczniki-tu',
                title: 'TU (załączniki do umowy)',
                disabled: !userPermissions.contract.read,
              },
              {
                icon: 'list',
                route: '/umowy/nowe-zalaczniki-tu',
                title: 'Nowe TU (załączniki do umowy)',
                disabled: !userPermissions.contract.read,
              },
              {
                icon: 'list',
                route: '/umowy/nowe',
                title: 'Umowy Nowe ',
                disabled: !userPermissions.contract.read,
                hidden: !userPermissions.contract.write,
              },
              {
                icon: 'list',
                route: '/umowy/finansowe',
                title: 'Umowy Finansowe Nowe',
                disabled: !userPermissions.contract.read,
                hidden: !userPermissions.contract.write,
              },
              {
                icon: 'list',
                route: '/umowy/dobry-agent',
                title: 'Umowy Dobry Agent Nowe',
                disabled: !userPermissions.contract.read,
                hidden: !userPermissions.contract.write,
              },
              {
                icon: 'list',
                route: '/umowy/samofakturowanie',
                title: 'Samofakturowanie Nowe',
                disabled: !userPermissions.contract.read,
                hidden: !userPermissions.contract.write,
              },
              {
                icon: 'list',
                route: '/umowy/do-rozwiazania-dobry-agent',
                title: 'Umowy Dobry Agent Do Rozwiązania ',
                disabled: !userPermissions.contract.read,
                hidden: !userPermissions.contract.write,
              },
              {
                icon: 'list',
                route: '/umowy/do-rozwiazania',
                title: 'Umowy Do rozwiązania ',
                disabled: !userPermissions.contract.read,
                hidden: !userPermissions.contract.write,
              },
              {
                icon: 'list',
                route: '/umowy/do-rozwiazania-finansowe',
                title: 'Umowy Finansowe Do rozwiązania',
                disabled: !userPermissions.contract.read,
                hidden: !userPermissions.contract.write,
              },
              {
                icon: 'list',
                route: '/umowy/do-rozwiazania-samofakturowanie',
                title: 'Samofakturowanie Do rozwiązania',
                disabled: !userPermissions.contract.read,
                hidden: !userPermissions.contract.write,
              }
            ]
          },
        ]
      },
      {
        name: 'Rejestracje',
        icon: 'school',
        sidebarConfigs: [
          {
            title: 'KRK',
            show: false,
            icon: 'gavel',
            parentPath: '/krk',
            disabled: !userPermissions.krk.read,
            items: [
              {
                icon: 'add',
                route: '/krk/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.krk.write,
              },
              {
                icon: 'list',
                route: '/krk',
                title: 'Lista',
                disabled: !userPermissions.krk.read,
              },
              {
                icon: 'list',
                route: '/krk/do-wyrobienia',
                title: 'Do wyrobienia',
                disabled: !userPermissions.krk.read,
              },
            ]
          },
          {
            title: 'Rejestracje',
            show: false,
            icon: 'event_available',
            parentPath: '/rejestracje',
            disabled: !userPermissions.registration.read,
            items: [
              // {
              //   icon: 'add',
              //   route: '/rejestracje/dodaj',
              //   title: 'Dodaj',
              //   disabled: !userPermissions.registration.create,
              // },
              {
                icon: 'playlist_add',
                route: '/rejestracje/dodaj-wiele',
                title: 'Dodaj wiele',
                disabled: !userPermissions.registration.create,
              },
              {
                icon: 'list',
                route: '/rejestracje',
                title: 'Lista',
                disabled: !userPermissions.registration.read,
              },
              {
                icon: 'list',
                route: '/rejestracje/w-trakcie',
                title: 'W trakcie',
                disabled: !userPermissions.registration.read,
              },
              {
                icon: 'list',
                route: '/rejestracje/do-zapisania-na-egzamin',
                title: 'Do zapisania na egzamin',
                disabled: !userPermissions.registration.read,
              },
              {
                icon: 'list',
                route: '/rejestracje/do-zarejestrowania-w-tu',
                title: 'Do zarejestrowania w TU',
                disabled: !userPermissions.registration.read,
              },
              {
                icon: 'list',
                route: '/rejestracje/upowaznienia-do-wygenerowania',
                title: 'Upoważnienia do wygenerowania',
                disabled: !userPermissions.registration.read,
              },
              {
                icon: 'list',
                route: '/rejestracje/do-zablokowania',
                title: 'Do zablokowania',
                disabled: !userPermissions.registration.read,
                badge: this.notificationService.registrationsToBlockCount,
              },
              {
                icon: 'list',
                route: '/rejestracje/do-wyrejestrowania',
                title: 'Do wyrejestrowania',
                disabled: !userPermissions.registration.read,
              },
              {
                icon: 'list',
                route: '/rejestracje/status-egzaminow',
                title: 'Status egzaminów',
                disabled: !userPermissions.registration.read,
              },
              {
                icon: 'list',
                route: '/rejestracje/do-weryfikacji-pzu',
                title: 'Do weryfikacji PZU',
                disabled: !userPermissions.registration.read,
              },
              {
                icon: 'list',
                route: '/rejestracje/do-weryfikacji-pzu-zycie',
                title: 'Do weryfikacji PZU Życie',
                disabled: !userPermissions.registration.read,
              }
            ]
          },
          {
            title: ' Podwójne rejestracje',
            show: false,
            icon: 'event_available',
            parentPath: '/podwojna-rejestracje',
            disabled: !userPermissions.registration.read,
            items: [
              {
                icon: 'list',
                route: '/podwojna-rejestracje/w-trakcie',
                title: 'W trakcie',
                disabled: !userPermissions.registration.read,
              },
              {
                icon: 'list',
                route: '/podwojna-rejestracje/do_weryfikacji',
                title: 'Do weryfikacji',
                disabled: !userPermissions.registration.read,
              },
            ]
          },
          {
            title: 'Brakujące dokumenty',
            show: false,
            icon: 'rule',
            parentPath: '/brakujace_dokumenty',
            disabled: !userPermissions.registration.read,
            items: [],
            route: '/brakujace_dokumenty'

          },

          // {
          //   title: 'Chronie.pl',
          //   show: false,
          //   icon: 'event_available',
          //   parentPath: '/chroniepl',
          //   disabled: !userPermissions.registration.read,
          //   items: [
          //     {
          //       icon: 'list',
          //       route: '/chroniepl',
          //       title: 'Lista',
          //       disabled: !userPermissions.registration.read,
          //     },
          //     {
          //       icon: 'list',
          //       route: '/chroniepl/nowe',
          //       title: 'Nowe dostępy',
          //       disabled: !userPermissions.registration.read,
          //     },
          //     {
          //       icon: 'list',
          //       route: '/chroniepl/do-zablokowania',
          //       title: 'Do zablokowania',
          //       disabled: !userPermissions.registration.read,
          //     },
          //   ]
          // },

          {
            title: 'Identyfikatory',
            show: false,
            icon: 'fingerprint',
            parentPath: '/identyfikatory',
            disabled: !userPermissions.registration.read,
            items: [
              {
                icon: 'publish',
                route: '/identyfikatory/import',
                title: 'Importuj',
                disabled: !userPermissions.registration.id_import,
              },
              {
                icon: 'list',
                route: '/identyfikatory/importy',
                title: 'Lista importów',
                disabled: !userPermissions.registration.id_import,
              },
              {
                icon: 'list',
                route: '/identyfikatory',
                title: 'Lista',
                disabled: !userPermissions.registration.read,
              },
              {
                title: 'Szablon email TU',
                icon: 'description',
                route: '/identyfikatory/szablon-email',
                disabled: !userPermissions.registration.id_import,
              },
            ]
          },

          {
            title: 'Wyrejestrowania',
            show: false,
            icon: 'event_busy',
            parentPath: '/wyrejestrowania',
            disabled: !userPermissions.deregistration.read,
            items: [
              {
                icon: 'add',
                route: '/wyrejestrowania/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.deregistration.write,
              },
              {
                icon: 'list',
                route: '/wyrejestrowania',
                title: 'Lista',
                disabled: !userPermissions.deregistration.read,
              }
            ]
          },
          {
            title: 'Egzaminy',
            show: false,
            icon: 'edit_calendar',
            parentPath: '/egzaminy-szkolenia',
            disabled: !userPermissions.meeting.read,
            items: [
              {
                icon: 'add',
                route: '/egzaminy-szkolenia/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.meeting.write,
              },
              {
                icon: 'list',
                route: '/egzaminy-szkolenia/egzaminy',
                title: 'Lista',
                disabled: !userPermissions.meeting.read,
              },
              {
                icon: 'calendar_today',
                route: '/egzaminy-szkolenia/kalendarz',
                title: 'Kalendarz',
                disabled: !userPermissions.meeting.read,
              },
              {
                icon: 'checklist',
                route: '/egzaminy-szkolenia/zapisane-na-egzamin',
                title: 'OFWCA zapisane na egzamin',
                disabled: !userPermissions.meeting.read,
              }
            ]
          },
          {
            title: 'Wymogi IDD',
            show: false,
            icon: 'school',
            parentPath: '/szkolenia-idd',
            disabled: !userPermissions.idd_training.read,
            items: [
              {
                icon: 'add',
                route: '/szkolenia-idd/dodaj',
                title: 'Dodaj zaświadczenia',
                disabled: !userPermissions.idd_training.write,
              },
              {
                icon: 'add',
                route: '/szkolenia-idd/wczytaj',
                title: 'Wczytaj zaświadczenia',
                disabled: !userPermissions.idd_training.write,
              },
              {
                icon: 'list',
                route: '/szkolenia-idd',
                title: ' Lista wymogów IDD',
                disabled: !userPermissions.idd_training.read,
              },
              {
                icon: 'list',
                route: '/szkolenia-idd/zaswiadczenia',
                title: 'Zaświadczenia',
                disabled: !userPermissions.idd_training.read,
              },
              {
                icon: 'list',
                route: '/szkolenia-idd/zaswiadczenia360',
                title: 'Zaświadczenia ASF360',
                disabled: !userPermissions.idd_training.read,
              }
            ]
          },
          {
            title: 'Szkolenia',
            show: false,
            icon: 'edit_calendar',
            parentPath: '/szkolenie',
            disabled: !userPermissions.meeting.read,
            items: [
              {
                icon: 'add',
                route: '/szkolenie/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.meeting.create,
              },
              {
                icon: 'list',
                route: '/szkolenie',
                title: 'Lista',
                disabled: !userPermissions.meeting.read,
              },
              {
                icon: 'calendar_today',
                route: '/szkolenie/kalendarz',
                title: 'Kalendarz',
                disabled: !userPermissions.meeting.read,
              },
              {
                icon: 'checklist',
                route: '/szkolenie/zapisane-na-szkolenia',
                title: 'OFWCA zapisane na szkolenie',
                disabled: !userPermissions.meeting.read,
              },
              {
                icon: 'group',
                route: '/szkolenie/szkoleniowcy',
                title: 'Szkoleniowcy',
                disabled: !userPermissions.idd_training.write,
              }
            ]
          },
          {
            title: 'Dokumenty',
            show: false,
            icon: 'file_present',
            parentPath: '/dokumenty/rejestracje',
            disabled: !userPermissions.document.read,
            items: [
              {
                icon: 'add',
                route: '/dokumenty/rejestracje/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.document.write,
              },
              {
                icon: 'list',
                route: '/dokumenty/rejestracje',
                title: 'Lista',
                disabled: !userPermissions.document.read,
              },
              {
                icon: 'add',
                route: '/dokumenty/rejestracje/szablony/dodaj',
                title: 'Szablony dodaj',
                disabled: !userPermissions.template.write,
              },
              {
                icon: 'list',
                route: '/dokumenty/rejestracje/szablony',
                title: 'Szablony lista',
                disabled: !userPermissions.template.read,
              },
              {
                icon: 'fact_check',
                route: '/dokumenty/rejestracje/szablony/konfiguracja',
                title: 'Szablony lista pól',
                disabled: !userPermissions.template.read,
              }
            ]
          },
          {
            title: 'Zmiana danych osobowych',
            show: false,
            icon: 'history_edu',
            parentPath: '/zmiana-danych-osobowych',
            disabled: !userPermissions.personal_data_change.read,
            items: [
              {
                icon: 'list',
                route: '/zmiana-danych-osobowych/lista',
                title: 'Lista',
                disabled: !userPermissions.personal_data_change.read,
              },
              {
                icon: 'list',
                route: '/zmiana-danych-osobowych/zgloszone',
                title: 'Zgłoszone',
                disabled: !userPermissions.personal_data_change.read,
              },
              {
                icon: 'list',
                route: '/zmiana-danych-osobowych/w-toku',
                title: 'W toku',
                disabled: !userPermissions.personal_data_change.read,
              },
              {
                icon: 'list',
                route: '/zmiana-danych-osobowych/zrealizowane',
                title: 'Zrealizowane',
                disabled: !userPermissions.personal_data_change.read,
              },
            ]
          },
        ],
      },
      {
        name: 'Rozliczenia',
        icon: 'attach_money',
        sidebarConfigs: [
          {
            title: 'Raporty TU',
            show: false,
            icon: 'calculate',
            parentPath: '/raporty-tu',
            disabled: !userPermissions.settlement.read,
            items: [
              {
                icon: 'publish',
                route: '/raporty-tu/importuj',
                title: 'Importuj',
                disabled: !userPermissions.settlement.write,
              },
              {
                icon: 'list',
                route: '/raporty-tu',
                title: 'Lista importów',
                disabled: !userPermissions.settlement.read,
              },
              {
                icon: 'list',
                route: '/raporty-tu/prowizje',
                title: 'Prowizje',
                disabled: !userPermissions.settlement.read,
              },
              {
                icon: 'list',
                route: '/raporty-tu/prowizje/archiwum',
                title: 'Prowizje archiwum',
                disabled: !userPermissions.settlement.read,
              }
            ]
          },
          {
            title: 'Masowa edycja',
            show: false,
            icon: 'edit_note',

            parentPath: '/masowa-edycja',
            disabled: !userPermissions.settlement.write,
            items: [
              {
                icon: 'list',
                route: '/masowa-edycja/aktualne',
                title: 'Aktualne',
                disabled: !userPermissions.settlement.write,
              },
              {
                icon: 'list',
                route: '/masowa-edycja/nowe',
                title: 'Nowe',
                disabled: !userPermissions.settlement.write,
              },
              {
                icon: 'list',
                route: '/masowa-edycja/odrzucone',
                title: 'Odrzucone',
                disabled: !userPermissions.settlement.write,
              },
            ]
          },
          {
            title: 'Generowanie',
            show: false,
            icon: 'add_task',
            items: [],
            route: '/generowanie',
            parentPath: '/generowanie',
            disabled: !userPermissions.settlement.read,
          },
          {
            title: 'Tabele prowizyjne',
            show: false,
            icon: 'border_all',
            route: '/tabele-prowizyjne',
            parentPath: '/tabele-prowizyjne',
            disabled: !userPermissions.commission_table.delete,
            items: []
          },{
            title: 'Ryzyka',
            show: false,
            icon: 'calculate',
            parentPath: '/ryzyka',
            disabled: !userPermissions.settlement.read,
            items: [
              {
                icon: 'add',
                route: '/ryzyka/dodaj',
                title: 'Dodaj ryzyko',
                disabled: !userPermissions.settlement.write,
              },
              {
                icon: 'list',
                route: '/ryzyka',
                title: 'Lista ryzyk',
                disabled: !userPermissions.settlement.read,
              }
            ]
          },
          {
            title: 'Aneksy',
            show: false,
            icon: 'inventory',
            parentPath: '/aneksy',
            disabled: !userPermissions.settlement.read,
            items: [
              {
                icon: 'add',
                route: '/aneksy/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.settlement.write,
              },
              {
                icon: 'list',
                route: '/aneksy',
                title: 'Lista',
                disabled: !userPermissions.settlement.read,
              }
            ]
          },
          {
            title: 'Przepięcia polis',
            show: false,
            icon: 'social_distance',
            parentPath: '/przepiecia-polis',
            disabled: !userPermissions.commission_redirection.read,
            items: [
              {
                icon: 'add',
                route: '/przepiecia-polis/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.commission_redirection.write,
              },
              {
                icon: 'publish',
                route: '/przepiecia-polis/importuj',
                title: 'Importuj',
                disabled: !userPermissions.commission_redirection.import,
              },
              {
                icon: 'list',
                route: '/przepiecia-polis/lista-importow',
                title: 'Lista importów',
                disabled: !userPermissions.commission_redirection.import,
              },
              {
                icon: 'list',
                route: '/przepiecia-polis',
                title: 'Lista',
                disabled: !userPermissions.commission_redirection.read,
              }
            ]
          },
          {
            title: 'Słownik progów',
            show: false,
            icon: 'library_books',
            parentPath: '/progi',
            disabled: !userPermissions.settlement.read,
            items: [
              {
                icon: 'add',
                route: '/progi/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.settlement.write,
              },
              {
                icon: 'list',
                route: '/progi',
                title: 'Lista',
                disabled: !userPermissions.settlement.read,
              }
            ]
          },

          {
            title: 'Słownik ryzyk',
            show: false,
            icon: 'library_books',
            parentPath: '/slownik-ryzyk',
            disabled: !userPermissions.settlement.read,
            items: [
              {
                icon: 'add',
                route: '/slownik-ryzyk/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.settlement.write,
              },
              {
                icon: 'list',
                route: '/slownik-ryzyk',
                title: 'Lista',
                disabled: !userPermissions.settlement.read,
              }
            ]
          },
          {
            title: 'Konfigurator',
            show: false,
            icon: 'tune',
            route: '/konfigurator',
            parentPath: '/konfigurator',
            disabled: !userPermissions.settlement.read,
            items: []
          },
          {
            title: 'Rozliczenia',
            show: false,
            icon: 'attach_money',
            parentPath: '/rozliczenia',
            disabled: !userPermissions.settlement.read,
            items: [
              {
                icon: 'list',
                route: '/rozliczenia',
                title: 'Lista',
                disabled: !userPermissions.settlement.write,
              },
              {
                icon: 'list',
                route: '/rozliczenia/salda',
                title: 'Salda',
                disabled: !userPermissions.settlement.write,
              },
              {
                title: 'Szablon email',
                icon: 'description',
                route: 'rozliczenia/szablon-email',
                disabled: !userPermissions.settlement.write,
              },
            ],
          },
          {
            title: 'Faktury',
            show: false,
            icon: 'account_balance',
            parentPath: '/faktury',
            disabled: !userPermissions.settlement.read,
            items: [
              {
                icon: 'add',
                route: '/faktury/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.settlement.write,
              },
              {
                icon: 'add',
                route: '/faktury/dodaj-rachunki',
                title: 'Dodaj rachunki',
                disabled: !userPermissions.settlement.write,
              },
              {
                icon: 'add',
                route: '/faktury/generator',
                title: 'Generator',
                disabled: !userPermissions.settlement.write,
              },
              {
                icon: 'list',
                route: '/faktury',
                title: 'Lista',
                disabled: !userPermissions.settlement.read,
              },
              {
                icon: 'list',
                route: '/faktury/lista-do-przelewu-adamczuk',
                title: 'Lista do przelewu Adamczuk',
                disabled: !userPermissions.settlement.read,
              },
              {
                icon: 'list',
                route: '/faktury/lista-do-przelewu-asf-premium',
                title: 'Lista do przelewu ASF Premium ',
                disabled: !userPermissions.settlement.read,
              },
              {
                icon: 'list',
                route: '/faktury/lista-do-przelewu-multisfera',
                title: 'Lista do przelewu MULTISFERA ',
                disabled: !userPermissions.settlement.read,
              },
              {
                icon: 'list',
                route: '/faktury/lista-plikow-do-banku',
                title: 'Lista plików do banku',
                disabled: !userPermissions.settlement.admin_root,
                hidden: !userPermissions.settlement.admin_root,

              },
            ]
          },
          {
            title: 'Noty',
            show: false,
            icon: 'account_balance',
            parentPath: '/noty',
            disabled: !userPermissions.settlement.read,
            items: [
              {
                icon: 'add',
                route: '/noty/dodaj',
                title: 'Dodaj notę',
                disabled: !userPermissions.settlement.write,
              },
              {
                icon: 'list',
                route: '/noty',
                title: 'Lista',
                disabled: !userPermissions.settlement.read,
              },
            ]
          },
          {
            title: 'Marżowość',
            show: false,
            icon: 'attach_money',
            route: '/marzowosc',
            parentPath: '/marzowosc',
            disabled: !userPermissions.settlement.margin,
            items: [],
          },
          {
            title: 'Automatyczne odrzucanie',
            show: false,
            icon: 'list',
            route: '/automatyczne-odrzucanie',
            parentPath: '/automatyczne-odrzucanie',
            disabled: !userPermissions.settlement.delete,
            items: []
          }
        ],
      },  {
        name: 'Należności',
        icon: 'payments',
        disabled: !userPermissions.receivables.limited_read,
        sidebarConfigs: [
          {
          title: 'Raporty',
          show: false,
          icon: 'calculate',
          parentPath: '/naleznosci/raporty',
          disabled: !userPermissions.receivables.read,
          items: [
            {
              icon: 'publish',
              route: '/naleznosci/raporty/importuj',
              title: 'Import',
              disabled: !userPermissions.receivables.write,
            },
            {
              icon: 'list',
              route: '/naleznosci/raporty',
              title: 'Lista',
              disabled: !userPermissions.receivables.read,
            }
          ]
        }, {
            title: 'Należności',
            show: false,
            icon: 'calculate',
            parentPath: '/naleznosci/lista',
            disabled: !userPermissions.receivables.read,
            items: [
              {
                icon: 'list',
                route: '/naleznosci/lista',
                title: 'Wszystkie',
                disabled: !userPermissions.receivables.read,
              },
              {
                icon: 'list',
                route: '/naleznosci/lista/do_weryfikacji',
                title: 'Do weryfikacji',
                disabled: !userPermissions.receivables.read,
              },
              {
                icon: 'list',
                route: '/naleznosci/lista/do_anulowania',
                title: 'Do anulowania',
                disabled: !userPermissions.receivables.read,
              },
              {
                icon: 'list',
                route: '/naleznosci/lista/nowa',
                title: 'Nowa',
                disabled: !userPermissions.receivables.read,
              },
              {
                icon: 'list',
                route: '/naleznosci/lista/nieoplacone',
                title: 'Nieopłacone',
                disabled: !userPermissions.receivables.read,
              },
              {
                icon: 'list',
                route: '/naleznosci/lista/oplacone',
                title: 'Opłacone',
                disabled: !userPermissions.receivables.read,
              },
              {
                icon: 'list',
                route: '/naleznosci/lista/anulowane',
                title: 'Anulowane',
                disabled: !userPermissions.receivables.read,
              },
              {
                icon: 'list',
                route: '/naleznosci/lista/wplaty_zgloszone',
                title: 'Wpłaty zgłoszone',
                disabled: !userPermissions.receivables.read,
              }
            ]
          },
          {
            title: 'Generowanie',
            show: false,
            icon: 'calculate',
            route: '/naleznosci/generowanie',
            parentPath: '/naleznosci/generowanie',
            disabled: !userPermissions.receivables.write,
            items: []
          },
          {
            title: 'Wpłaty',
            show: false,
            icon: 'calculate',
            parentPath: '/naleznosci/wplaty',
            disabled: !userPermissions.receivables.read,
            items: [
              {
                icon: 'add',
                route: '/naleznosci/wplaty/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.receivables.write,
              },
              {
                icon: 'add',
                route: '/naleznosci/wplaty/przeksiegowania',
                title: 'Przeksięgowania',
                disabled: !userPermissions.receivables.write,
              },
              {
                icon: 'publish',
                route: '/naleznosci/wplaty/import',
                title: 'Import',
                disabled: !userPermissions.receivables.write,
              },
              {
                icon: 'list',
                route: '/naleznosci/wplaty/lista_importow',
                title: 'Lista importów',
                disabled: !userPermissions.receivables.read,
              },
              {
                icon: 'list',
                route: '/naleznosci/wplaty/rozliczone',
                title: 'rozliczone',
                disabled: !userPermissions.receivables.read,
              },
              {
                icon: 'list',
                route: '/naleznosci/wplaty/nierozliczone',
                title: 'Nierozliczone',
                disabled: !userPermissions.receivables.read,
              },
              {
                icon: 'list',
                route: '/naleznosci/wplaty/anulowane',
                title: 'Anulowane',
                disabled: !userPermissions.receivables.read,
              },
              {
                icon: 'list',
                route: '/naleznosci/wplaty/przelewy-wychodzace',
                title: 'Przelewy wychodzace',
                disabled: !userPermissions.receivables.read,
              }
            ]
          },   {
            title: 'Salda',
            show: false,
            icon: 'calculate',
            route: '/naleznosci/salda/lista',
            parentPath: '/naleznosci/salda',
            disabled: !userPermissions.receivables.limited_read,
            items: []
          },
          {

            title: 'Przelewy',
            show: false,
            icon: 'account_balance',
            parentPath: '/naleznosci/przelewy',
            disabled: !userPermissions.receivables.delete,
            items: [
              {
                icon: 'add',
                route: '/naleznosci/przelewy/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.receivables.delete,
              },
              {
                icon: 'list',
                route: '/naleznosci/przelewy',
                title: 'Lista',
                disabled: !userPermissions.receivables.delete,
              },
              {
                icon: 'list',
                route: '/naleznosci/przelewy/do-zrealizowania',
                title: 'Do zrealizowania',
                disabled: !userPermissions.receivables.delete
              },
              {
                icon: 'account_balance',
                route: '/naleznosci/przelewy/pliki/lista',
                title: 'Wygenerowane pliki',
                disabled: !userPermissions.receivables.delete,
              }
            ]
          }
        ]
      }, {
        name: 'Terminale',
        icon: 'sentiment_satisfied',
        disabled: !userPermissions.terminals.read,
        sidebarConfigs: [
          {
            title: 'Raporty',
            show: false,
            icon: 'calculate',
            parentPath: '/terminale/raporty',
            disabled: !userPermissions.terminals.read,
            items: [
              {
                icon: 'publish',
                route: '/terminale/raporty/importuj',
                title: 'Import',
                disabled: !userPermissions.terminals.write,
              },
              {
                icon: 'list',
                route: '/terminale/raporty',
                title: 'Lista',
                disabled: !userPermissions.terminals.read,
              }
            ]
          },
          {
            title: 'Transakcje',
            show: false,
            icon: 'calculate',
            parentPath: '/terminale/transakcje',
            disabled: !userPermissions.terminals.read,
            items: [
              {
                icon: 'list',
                route: '/terminale/transakcje/rozliczone',
                title: 'Rozliczone',
                disabled: !userPermissions.terminals.read,
              },
              {
                icon: 'list',
                route: '/terminale/transakcje/nierozliczone',
                title: 'Nierozliczone',
                disabled: !userPermissions.terminals.read,
              },
              {
                icon: 'list',
                route: '/terminale/transakcje/do-weryfikacji',
                title: 'Do weryfikacji',
                disabled: !userPermissions.terminals.read,
              }
            ]
          },
          {
            title: 'Polisy',
            show: false,
            icon: 'calculate',
            parentPath: '/terminale/polisy',
            disabled: !userPermissions.terminals.read,
            items: [
              {
                icon: 'list',
                route: '/terminale/polisy/lista',
                title: 'lista',
                disabled: !userPermissions.terminals.read,
              },
              {
                icon: 'list',
                route: '/terminale/polisy/zaplacone',
                title: 'Zapłacone',
                disabled: !userPermissions.terminals.read,
              },
              {
                icon: 'list',
                route: '/terminale/polisy/nierozliczone',
                title: 'Nierozliczone',
                disabled: !userPermissions.terminals.read,
              }
            ]
          },
          {
            title: 'Przelewy',
            show: false,
            icon: 'calculate',
            parentPath: '/terminale/przelewy',
            disabled: !userPermissions.terminals.read,
            items: [
              {
                icon: 'list',
                route: '/terminale/przelewy/polisy/lista',
                title: 'Polisy do przelewu',
                disabled: !userPermissions.terminals.read,
              },
              {
                icon: 'list',
                route: 'terminale/przelewy/przelewy/lista',
                title: 'Wygenerowane pliki',
                disabled: !userPermissions.terminals.read,
              }
            ]
          }
        ]
      },
      {
        name: 'Analizy',
        icon: 'analytics',
        disabled: !userPermissions.analysis.read_report,
        sidebarConfigs: [
          {
            title: 'Raporty',
            show: false,
            icon: 'download',
            items: [],
            route: '/analizy/raporty',
            parentPath: '/analizy/raporty',
            disabled: !userPermissions.analysis.read_report,
          },
          {
            title: 'OFWCA',
            show: false,
            icon: 'group',
            items: [],
            route: '/analizy/ofwca',
            parentPath: '/analizy/ofwca',
            disabled: !userPermissions.analysis.read_report,
          },
          {
            title: 'Partner',
            show: false,
            icon: 'account_circle',
            items: [],
            route: '/analizy/partner',
            parentPath: '/analizy/partner',
            disabled: !userPermissions.analysis.read_report,
          },
          {
            title: 'Umowy',
            show: false,
            icon: 'history_edu',
            items: [],
            route: '/analizy/umowy',
            parentPath: '/analizy/umowy',
            disabled: !userPermissions.analysis.read_report,
          },
          {
            title: 'Rejestracje',
            show: false,
            icon: 'event_available',
            items: [],
            route: '/analizy/rejestracje',
            parentPath: '/analizy/rejestracje',
            disabled: !userPermissions.analysis.read_report,
          },
          {
            title: 'Identyfikatory',
            show: false,
            icon: 'fingerprint',
            items: [],
            route: '/analizy/identyfikatory',
            parentPath: '/analizy/identyfikatory',
            disabled: !userPermissions.analysis.read_report,
          },
          {
            title: 'Przepięcia polis',
            show: false,
            icon: 'social_distance',
            items: [],
            route: '/analizy/przepięcia-polis',
            parentPath: '/analizy/przepięcia-polis',
            disabled: !userPermissions.analysis.read_report,
          }
        ]
      }, {
        name: 'Sprzedaż',
        disabled: true,
        icon: 'leaderboard',
        sidebarConfigs: [
          {
            title: 'Importuj',
            show: false,
            icon: 'publish',
            items: [],
            route: '/sprzedaz-importuj',
            parentPath: '/sprzedaz-importuj',
            disabled: true, // !userPermissions.sold_policy.policy_import,
          },
          {
            title: 'Lista importów',
            show: false,
            icon: 'list',
            items: [],
            route: '/sprzedaz-lista-importow',
            parentPath: '/sprzedaz-lista-importow',
            disabled: true, // !userPermissions.sold_policy.policy_import,
          },
          {
            title: 'Lista polis',
            show: false,
            icon: 'list',
            items: [],
            route: '/sprzedaz-lista-polis',
            parentPath: '/sprzedaz-lista-polis',
            disabled: true, // !userPermissions.sold_policy.read,
          },
          {
            title: 'Raporty',
            show: false,
            icon: 'summarize',
            items: [],
            route: '/sprzedaz-raporty',
            parentPath: '/sprzedaz-raporty',
            disabled: true, // !userPermissions.sold_policy.read_report,
          },
        ],
      },
      {
        name: 'Newsletter',
        icon: 'newspaper',
        route: '/newsletter',
        disabled: !userPermissions.newsletter.read,
        sidebarConfigs: [
          {
            title: 'Dodaj',
            show: false,
            icon: 'add',
            route: '/newsletter/dodaj',
            parentPath: '/newsletter',
            disabled: !userPermissions.newsletter.write,
            items: []
          },
          {
            title: 'Aktualne',
            show: false,
            icon: 'newspaper',
            route: '/newsletter',
            parentPath: '/newsletter',
            disabled: !userPermissions.newsletter.read,
            items: []
          },
          {
            title: 'Historia',
            show: false,
            icon: 'list',
            route: '/newsletter/historia',
            parentPath: '/newsletter',
            disabled: !userPermissions.newsletter.read,
            items: []
          }
        ]
      },
      {
        name: 'Obsługa',
        icon: 'support_agent',
        sidebarConfigs: [
          {
            title: 'Wsparcie',
            show: false,
            icon: 'support_agent',
            parentPath: '/support',
            disabled: !userPermissions.support.read,
            items: [
              {
                title: 'Dodaj',
                icon: 'add',
                route: '/support/dodaj',
                disabled: !userPermissions.support.create,
              },
              {
                title: 'Lista',
                icon: 'list',
                route: '/support',
                disabled: !userPermissions.support.read,
              }
            ]
          },
          {
            title: 'Korporacja',
            show: false,
            icon: 'corporate_fare',
            parentPath: '/korporacja',
            disabled: !userPermissions.corporation.read,
            items: [
              {
                title: 'Dodaj',
                icon: 'add',
                route: '/korporacja/dodaj',
                disabled: !userPermissions.corporation.create,
              },
              {
                title: 'Lista',
                icon: 'list',
                route: '/korporacja',
                disabled: !userPermissions.corporation.read,
              }
            ]
          }
        ]
      },
      {
        name: 'Kadry',
        icon: 'miscellaneous_services',
        disabled: !userPermissions.human_resources.admin_root ,
        sidebarConfigs: [
          {
            title: 'Pracownicy',
            show: false,
            icon: 'group',
            parentPath: '/kadry',
            disabled: !userPermissions.human_resources.admin_root,
            items: [],
            route: '/kadry',
          },
          {
            title: 'Wnioski urlopowe',
            show: false,
            icon: 'group',
            parentPath: '/kadry/wnioski-urlopowe',
            disabled: !userPermissions.human_resources.admin_root,
            items: [
              {
                icon: 'list',
                route: '/kadry/wnioski-urlopowe',
                title: 'Wszystkie',
                disabled: !userPermissions.human_resources.admin_root,
              },
              {
                icon: 'list',
                route: '/kadry/wnioski-urlopowe/do-akceptacji',
                title: ' Do akceptacji',
                disabled: !userPermissions.human_resources.admin_root,
              },
              {
                icon: 'list',
                route: '/kadry/wnioski-urlopowe/zaakceptowany',
                title: 'Zaakceptowany',
                disabled: !userPermissions.human_resources.admin_root,
              },
              {
                icon: 'list',
                route: '/kadry/wnioski-urlopowe/anulowany',
                title: 'Anulowany',
                disabled: !userPermissions.human_resources.admin_root,
              },
              {
                icon: 'list',
                route: '/kadry/wnioski-urlopowe/odrzucony',
                title: 'Odrzucony',
                disabled: !userPermissions.human_resources.admin_root,
              },
            ],
          }
        ]
      },
      {
        name: 'Marketing',
        icon: 'miscellaneous_services',
        disabled: !userPermissions.marketing.read ,
        sidebarConfigs: [
          {
            title: 'Stan magazynowy',
            show: false,
            icon: 'storage',
            items: [],
            route: '/stan-magazynowy',
            parentPath: '/stan-magazynowy',
            disabled: !userPermissions.marketing.read,
          },
          {
            title: 'Nagrody',
            show: false,
            icon: 'group',
            parentPath: '/wyniki-konkursow',
            disabled: !userPermissions.marketing.read,
            items: [
              {
                icon: 'upload',
                route: 'wyniki-konkursow/import',
                title: 'Import danych',
                disabled: !userPermissions.marketing.read_report,
              },
              {
                icon: 'list',
                route: 'wyniki-konkursow/lista-importow',
                title: 'Lista importów',
                disabled: !userPermissions.marketing.read,
              },
              {
                icon: 'list',
                route: 'wyniki-konkursow/pola-dodatkowe',
                title: 'Pola dodatkowe',
                disabled: !userPermissions.marketing.read,
              },
              {
                icon: 'list',
                route: 'wyniki-konkursow/wyniki',
                title: 'Lista nagród',
                disabled: !userPermissions.marketing.read,
              },
            ],
          },
          {
            title: 'Wydania z magazynu',
            show: false,
            icon: 'group',
            parentPath: '/wydania-z-magazynu',
            disabled: !userPermissions.marketing.read,
            items: [
              {
                icon: 'list',
                route: 'wydania-z-magazynu/lista',
                title: 'Wydania z magazynu',
                disabled: !userPermissions.marketing.read,
              },
              {
                icon: 'list',
                route: 'wydania-z-magazynu/dokumenty',
                title: 'Dokumenty',
                disabled: !userPermissions.marketing.read,
              },
            ],
          },
        ]
      },
      {
        name: 'Narzędzia',
        icon: 'miscellaneous_services',
        disabled: !userPermissions.form.read && !userPermissions.file_manager.read && !userPermissions.mailing.read &&
          !userPermissions.file_sharing.read && !userPermissions.asf_360.read,
        sidebarConfigs: [
          {
            title: 'Formularze',
            show: false,
            icon: 'file_present',
            parentPath: '/formularze',
            disabled: !userPermissions.form.read,
            items: [],
            route: '/formularze',
          },
          {
            title: 'Baza wiedzy',
            show: false,
            icon: 'folder',
            parentPath: '/serwis-plikow',
            disabled: !userPermissions.file_manager.read,
            items: [
              {
                icon: 'list',
                route: '/serwis-plikow/tag',
                title: 'Tagi',
                disabled: !userPermissions.file_manager.read,
              },
              {
                icon: 'list',
                route: '/serwis-plikow',
                title: 'Menedżer plików',
                disabled: !userPermissions.file_manager.read,
              },
              {
                icon: 'list',
                route: '/serwis-plikow/strona',
                title: 'Menedżer Stron',
                disabled: !userPermissions.file_manager.read,
              }
            ]
          },
          {
            title: 'Udostępnianie plików',
            show: false,
            icon: 'file_present',
            parentPath: '/udostepnianie',
            disabled: !userPermissions.file_sharing.read,
            items: [],
            route: '/udostepnianie',
          },
          {
            title: 'Mailing',
            show: false,
            icon: 'mail',
            parentPath: '/wysylka-maili',
            disabled: !userPermissions.mailing.read,
            items: [
              {
                icon: 'add',
                route: '/wysylka-maili/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.mailing.create,
              },
              {
                icon: 'list',
                route: '/wysylka-maili',
                title: 'Lista',
                disabled: !userPermissions.mailing.read,
              }
            ]
          },
          {
            title: 'ASF 360',
            show: false,
            icon: 'desktop_mac',
            parentPath: '/asf360',
            disabled: !userPermissions.asf_360.read,
            items: [
              {
                title: 'Posty',
                icon: 'maps_ugc',
                route: '/asf360/aktualnosci',
                disabled: !userPermissions.asf_360.read,
              },
              {
                title: 'Tagi',
                icon: 'tag',
                route: '/asf360/tagi',
                disabled: !userPermissions.asf_360.admin_root,
              },
              {
                title: 'Grupy uprawnień',
                icon: 'shield',
                route: '/asf360/tagi-uprawnienia',
                disabled: !userPermissions.asf_360.admin_root,
              },
            ]
          },
          {
            title: 'Dobry Agent',
            show: false,
            icon: 'newspaper',
            parentPath: '/blog-dobry-agent',
            disabled: !userPermissions.newsletter.read,
            items: [
              {
                title: 'Artykuły',
                icon: 'newspaper',
                route: '/blog-dobry-agent/artykuly',
                disabled: !userPermissions.newsletter.write,
              },
              {
                title: 'Autorzy',
                icon: 'people',
                route: '/blog-dobry-agent/autorzy',
                disabled: !userPermissions.newsletter.read,
              },

              {
                title: 'Kategorie',
                icon: 'tag',
                route: '/blog-dobry-agent/kategorie',
                disabled: !userPermissions.newsletter.read,
              },
              {
                title: 'Tagi',
                icon: 'tag',
                route: '/blog-dobry-agent/tagi',
                disabled: !userPermissions.newsletter.read,
              }
            ]
          },
        ]
      },
      {
        name: 'OCK',
        icon: 'sentiment_satisfied',
        disabled: !userPermissions.ock.create,
        sidebarConfigs: [
          {
            title: 'Raporty',
            show: false,
            icon: 'download',
            parentPath: '/ock/raporty',
            disabled: !userPermissions.ock.create,
            items: [
              {
                icon: 'list',
                route: '/ock/raporty/firma',
                title: 'Firmy',
                disabled: !userPermissions.ock.create,
              },
              {
                icon: 'list',
                route: '/ock/raporty/uzytkownicy',
                title: 'Użytkownicy',
                disabled: !userPermissions.ock.create,
              },
              {
                icon: 'list',
                route: '/ock/raporty/polisy',
                title: 'Polisy',
                disabled: !userPermissions.ock.create,
              }
            ]
          }, {
            title: 'Import',
            show: false,
            icon: 'upload',
            parentPath: '/ock/import',
            disabled: !userPermissions.ock.create,
            items: [
              {
                icon: 'list',
                route: '/ock/import/lista',
                title: 'Lista Importów',
                disabled: !userPermissions.ock.create,
              },
              {
                icon: 'add',
                route: '/ock/import/nip/dodaj',
                title: 'dodaj nip',
                disabled: !userPermissions.ock.create,
              },
              {
                icon: 'list',
                route: '/ock/import/nip',
                title: 'Lista nip',
                disabled: !userPermissions.ock.create,
              },
              {
                icon: 'add',
                route: '/ock/import/vin/dodaj',
                title: 'dodaj vin',
                disabled: !userPermissions.ock.create,
              },
              {
                icon: 'list',
                route: '/ock/import/vin',
                title: 'Lista vin',
                disabled: !userPermissions.ock.create,
              }
            ]
          }
        ]
      },
      {
        name: 'Administracja',
        icon: 'miscellaneous_services',
        sidebarConfigs: [

          {
            title: 'Pracownicy',
            show: false,
            icon: 'people',
            parentPath: '/pracownicy',
            disabled: !userPermissions.employees.read,
            items: [
              {
                icon: 'list',
                route: '/pracownicy',
                title: 'Lista',
                disabled: !userPermissions.employees.read,
              },
            ]
          },
          {
            title: 'Użytkownicy',
            show: false,
            icon: 'people',
            parentPath: '/uzytkownicy',
            disabled: !userPermissions.user.read,
            items: [
              {
                icon: 'add',
                route: '/uzytkownicy/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.user.write,
              },
              {
                icon: 'list',
                route: '/uzytkownicy',
                title: 'Lista',
                disabled: !userPermissions.user.read,
              },
              {
                icon: 'add',
                route: '/uzytkownicy/dzialy/dodaj',
                title: 'Dział uprawnień  dodaj',
                disabled: !userPermissions.department.write,
              },
              {
                icon: 'list',
                route: '/uzytkownicy/dzialy',
                title: 'Dział uprawnień  lista',
                disabled: !userPermissions.department.read,
              },
              {
                icon: 'list',
                route: '/uzytkownicy/regionalni',
                title: 'Regionalni lista',
                disabled: !userPermissions.user.write,
              },
              {
                icon: 'list',
                route: '/uzytkownicy/blokada-logowania',
                title: 'Blokada logowania',
                disabled: !userPermissions.user.admin_root ,
              }
            ]
          },
          {
            title: 'Import danych kontaktowych',
            show: false,
            icon: 'import_contacts',
            parentPath: '/import-danych-kontaktowych',
            disabled: !userPermissions.import_contacts.read,
            items: [],
            route: '/import-danych-kontaktowych',
          },
          // {
          //   title: 'Działy',
          //   show: false,
          //   icon: 'account_tree',
          //   parentPath: '/dzialy',
          //   disabled: !userPermissions.department.read,
          //   items: [
          //     {
          //       icon: 'add',
          //       route: '/dzialy/dodaj',
          //       title: 'Dodaj',
          //       disabled: !userPermissions.department.write,
          //     },
          //     {
          //       icon: 'list',
          //       route: '/dzialy',
          //       title: 'Lista',
          //       disabled: !userPermissions.department.read,
          //     },
          //   ]
          // },
          // {
          //   title: 'Regionalni',
          //   show: false,
          //   icon: 'account_circle',
          //   parentPath: '/regionalni',
          //   disabled: !userPermissions.user.write,
          //   items: [
          //     {
          //       icon: 'list',
          //       route: '/regionalni',
          //       title: 'Lista',
          //       disabled: !userPermissions.user.write,
          //     },
          //   ]
          // },
          // {
          //   title: 'Licencje Agent21',
          //   show: false,
          //   icon: 'content_copy',
          //   parentPath: '/licencje-agent21',
          //   disabled: !userPermissions.licensed_agent.read,
          //   items: [
          //     {
          //       icon: 'list',
          //       route: '/licencje-agent21',
          //       title: 'Lista',
          //       disabled: !userPermissions.licensed_agent.read,
          //     },
          //   ]
          // },
          {
            title: 'Adresy',
            show: false,
            icon: 'home',
            parentPath: '/baza-adresowa',
            disabled: !userPermissions.address_database.read,
            items: [
              {
                icon: 'list',
                route: '/baza-adresowa',
                title: 'Lista importów',
                disabled: !userPermissions.address_database.read,
              },
              {
                icon: 'add',
                route: '/baza-adresowa/dodaj',
                title: 'Dodaj ',
                disabled: !userPermissions.address_database.create,
              },
              {
                icon: 'list',
                route: '/baza-adresowa/lista',
                title: 'Lista ',
                disabled: !userPermissions.address_database.read,
              }
            ]
          },
          {
            title: 'Słownik',
            show: false,
            icon: 'settings',
            parentPath: '/dynamiczny-slownik',
            disabled: !userPermissions.dynamic_dictionary.read,
            items: [
              {
                icon: 'publish',
                route: '/dynamiczny-slownik/import',
                title: 'Import',
                disabled: !userPermissions.dynamic_dictionary.write,
                hidden: !userPermissions.dynamic_dictionary.write,
              },
              {
                icon: 'add',
                route: '/dynamiczny-slownik/dodaj',
                title: 'Dodaj',
                disabled: !userPermissions.dynamic_dictionary.write,
                hidden: !userPermissions.dynamic_dictionary.write,
              },
              {
                icon: 'list',
                route: '/dynamiczny-slownik',
                title: 'Lista',
                disabled: !userPermissions.dynamic_dictionary.read,
              }
            ]
          },
          {
            title: 'Urządzenia',
            show: false,
            icon: 'devices',
            parentPath: '/urzadzenia',
            disabled: !userPermissions.license.read,
            items: [
              {
                icon: 'list',
                route: '/urzadzenia/uzytkownicy',
                title: 'Użytkownicy',
                disabled: !userPermissions.license.read,
              },
              {
                icon: 'list',
                route: '/urzadzenia',
                title: 'Urządzenia',
                disabled: !userPermissions.license.read,
              },
              {
                icon: 'list',
                route: 'urzadzenia/licencje',
                title: 'Licencje',
                disabled: !userPermissions.license.read,
              },
            ],
          },
          {
            title: 'Chronie.pl',
            show: false,
            icon: 'shield',
            parentPath: '/chronie',
            disabled: !userPermissions.chronie.read,
            items: [
              {
                icon: 'list',
                route: '/chronie',
                title: 'Lista płatności',
                disabled: !userPermissions.chronie.read,
              },
              {
                icon: 'list',
                route: '/chronie/job',
                title: 'Status Workerów',
                disabled: !userPermissions.chronie.read,
              },
              {
                icon: 'settings',
                route: '/chronie/komunikaty-techniczne',
                title: 'Komunikat techniczny',
                disabled: !userPermissions.chronie.read,
              },
              {
                icon: 'list',
                route: '/chronie/direct',
                title: 'Direct',
                disabled: !userPermissions.chronie.read,
              }
            ]
          },
          {
            title: 'Hiperio',
            show: false,
            icon: 'shield',
            parentPath: '/hiperio',
            disabled: !userPermissions.hiperio.limited_read,
            items: [
              {
                icon: 'list',
                route: '/hiperio/loginy',
                title: 'Loginy',
                disabled: !userPermissions.hiperio.read,
                hidden:  !userPermissions.hiperio.read,
              },
              {
                icon: 'list',
                route: '/hiperio/konta',
                title: 'Profile',
                disabled: !userPermissions.hiperio.read,
                hidden:  !userPermissions.hiperio.read,
              },
              {
                icon: 'list',
                route: '/hiperio/loginy-testowe',
                title: 'Wnioski o dostępy testowe',
                disabled: !userPermissions.hiperio.limited_read,
              },
              {
                icon: 'list',
                route: '/hiperio/loginy-zmiany',
                title: 'Wnioski o zmianę loginu',
                disabled: !userPermissions.hiperio.limited_read,
              },
              {
                icon: 'list',
                route: '/hiperio/dostepy-testowe',
                title: 'Dostępy testowe ',
                disabled: !userPermissions.hiperio.read,
                hidden:  !userPermissions.hiperio.read,
              }
            ]
          },
          {
            title: 'DobryAgent.pl',
            show: false,
            icon: 'pin_drop',
            parentPath: '/dobry-agent',
            disabled: !userPermissions.dobry_agent.admin_root,
            items: [],
            route: '/dobry-agent',
          },
          {
            title: 'Import tagów',
            show: false,
            icon: 'tag',
            parentPath: '/import-tagow',
            disabled: !userPermissions.tag_import.read,
            items: [],
            route: '/import-tagow',
          },
          {
            title: 'Usunięte',
            show: false,
            icon: 'delete',
            parentPath: '/usuniete',
            disabled: !userPermissions.user.admin_root,
            items: [],
            route: '/usuniete',
          },
        ]
      },
      // {
      //   name: 'ASF360',
      //   icon: 'computer_desktop',
      //   url: 'assets/nav-asf360.png',
      //   disabled: !userApplications.asf360.active,
      //   link: '/rest/sso/api/redirect-token/360',
      //   sidebarConfigs: []
      // }
    ];
    this.pathsConfigSubject.next(this.pathsConfig);
  }

}
